import { z } from 'zod';

import type {
  MatchMethod,
  ProductMasterListQuery,
  ProductStatus,
  RunProductOutcome,
} from '../types/product-master.api.types';

/** Product types the "Type" switch narrows to (`kind`); "ALL" = no kind filter. */
export const PRODUCT_TYPES = ['UNIQUE', 'VARIANT', 'SET'] as const;
export type ProductTypeFilter = 'ALL' | (typeof PRODUCT_TYPES)[number];

/** Signal chips: independent boolean filters that AND together with the type and each other. */
export const PRODUCT_SIGNALS = ['duplicates', 'multiMarket', 'merged'] as const;
export type ProductSignal = (typeof PRODUCT_SIGNALS)[number];

/** Curation outcome buckets a run screen can drill into (`GET curation-runs/{id}/outcomes`). */
export const RUN_PRODUCT_OUTCOMES = [
  'NEW',
  'ATTACHED_AS_VARIANT',
  'MATCHED_EXISTING',
  'FIRST_CURATED',
  'NEEDS_ATTENTION',
  'SET_TOUCHED',
  'MERGED',
] as const satisfies readonly RunProductOutcome[];

/** How a feed row was matched — narrows a `MATCHED_EXISTING` drill-down. */
export const MATCH_METHODS = [
  'ITEM_GROUP_ID',
  'HANDLE',
  'ITEM_KEY',
  'GTIN',
  'MPN',
  'BRAND_TITLE',
  'FUZZY_TITLE',
  'MANUAL',
  'LEGACY',
] as const satisfies readonly MatchMethod[];

export const PRODUCT_STATUSES = [
  'ACTIVE',
  'SUPPRESSED',
] as const satisfies readonly ProductStatus[];

/** S3 — "Group by: none | Latest batch", grouping the current page only (no extra reads). */
export const PRODUCT_MASTER_GROUP_BYS = ['latestRun'] as const;
export type ProductMasterGroupBy = (typeof PRODUCT_MASTER_GROUP_BYS)[number];

/**
 * Connectors the product master can be narrowed to (the spec types `connectorCode` as a free
 * string) — the same set Needs attention offers, so no tenant's connector is unreachable.
 */
export const PRODUCT_MASTER_CONNECTORS = ['GOOGLE_MERCHANT', 'META_COMMERCE', 'SHOPIFY'] as const;

/**
 * URL search params of `/catalog-enrichment/products`. Every filter lives here so a run
 * screen can deep-link a drill-down (`?curatedInRun=<id>&outcome=NEW`) and Back
 * restores the list exactly; bad values are dropped rather than 422-ing the API.
 */
export const productMasterSearchSchema = z.object({
  type: z.enum(PRODUCT_TYPES).optional().catch(undefined),
  duplicates: z.boolean().optional().catch(undefined),
  multiMarket: z.boolean().optional().catch(undefined),
  merged: z.boolean().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  brand: z.string().optional().catch(undefined),
  market: z.string().optional().catch(undefined),
  connectorCode: z.string().optional().catch(undefined),
  status: z.enum(PRODUCT_STATUSES).optional().catch(undefined),
  includeDeleted: z.boolean().optional().catch(undefined),
  curatedInRun: z.string().uuid().optional().catch(undefined),
  outcome: z.enum(RUN_PRODUCT_OUTCOMES).optional().catch(undefined),
  matchMethod: z.enum(MATCH_METHODS).optional().catch(undefined),
  /** S3 — view preference, not a server filter: grouping happens client-side over the loaded page. */
  groupBy: z.enum(PRODUCT_MASTER_GROUP_BYS).optional().catch(undefined),
});

export type ProductMasterSearch = z.infer<typeof productMasterSearchSchema>;

/** Trimmed text filter, or null when blank — an empty value is "no filter", never sent. */
function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed;
}

/**
 * Builds the `GET catalog-products` query from the URL filters. `curatedInRun`
 * and `outcome` go together or not at all (either alone is a 422), and
 * `matchMethod` only narrows a `MATCHED_EXISTING` drill-down (the API 422s it with any other
 * outcome, so a hand-edited URL drops it instead).
 */
export function toProductMasterQuery(
  search: ProductMasterSearch,
  after: string | null,
  size: number,
): ProductMasterListQuery {
  const runScoped = !!search.curatedInRun && !!search.outcome;
  return {
    kind: search.type ?? null,
    hasDuplicates: search.duplicates ? true : null,
    multiMarket: search.multiMarket ? true : null,
    merged: search.merged ? true : null,
    q: blankToNull(search.q),
    brand: blankToNull(search.brand),
    market: blankToNull(search.market),
    connectorCode: blankToNull(search.connectorCode),
    status: search.status ?? null,
    includeDeleted: search.includeDeleted ?? false,
    curatedInRun: runScoped ? (search.curatedInRun ?? null) : null,
    outcome: runScoped ? (search.outcome ?? null) : null,
    matchMethod:
      runScoped && search.outcome === 'MATCHED_EXISTING' ? (search.matchMethod ?? null) : null,
    after,
    size,
  };
}
